import { Button } from "@egaranti/components";
import { useToast } from "@egaranti/components";
import { Tag } from "@egaranti/components";
import { Pagination } from "@egaranti/components";
import { Dialog, DialogContent } from "@egaranti/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@egaranti/components";
import { TableSkeleton } from "@egaranti/components";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import FilterIcon from "../../assets/icons/filter.svg?react";
import ImportIcon from "../../assets/icons/import.svg?react";

import productService from "@/services/productService";

import useProductsStoreFromIntegrations from "@/stores/useProductsFromIntegrationsStore";

import FilterSheet from "@/components/productsFromIntegrations/filterSheet";
import FilterTags from "@/components/productsFromIntegrations/filterTags";
import EmptyTable from "@/components/shared/EmptyTable";
import InfoTooltip from "@/components/shared/infoTooltip";
import PageSizeSelect from "@/components/shared/pageSizeSelect";
import TableHeaderWrapper from "@/components/shared/tableHeaderWrapper";
import BulkImportWizard from "@/components/ui/BulkImportWizard";

const Products = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [openFilterSheet, setOpenFilterSheet] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);

  const { products, loading, filters, getProducts, setFilters } =
    useProductsStoreFromIntegrations();

  useEffect(() => {
    getProducts();
  }, []);

  const onPageChange = (page) => {
    setFilters({ ...filters, page });
    getProducts();
  };

  const onPageSizeChange = (size) => {
    localStorage.setItem("pageSize", size);
    setFilters({ ...filters, size, page: 1 });
    getProducts();
  };

  const onClickItem = (e, id) => {
    e.stopPropagation();
    navigate(`/products/integrations/${id}`);
  };

  const onClickImport = () => {
    setIsBulkUploadOpen(true);
  };

  const bulkCreate = async (data) => {
    return productService.importProductsFromSource("API_INTEGRATION", data);
  };
  return (
    <main className="min-h-full bg-[#F9FAFC] p-4">
      <div className="flex items-center justify-end pb-4">
        <Button
          className="w-full sm:w-auto"
          variant="secondaryGray"
          onClick={onClickImport}
        >
          <ImportIcon />
          {t("common.import")}
        </Button>
      </div>
      <div className="rounded-lg">
        <div className="flex flex-col rounded-t-lg bg-white pb-4">
          <TableHeaderWrapper className="flex items-center justify-between gap-5">
            <FilterTags />
            <Button
              onClick={() => setOpenFilterSheet(true)}
              variant="secondaryGray"
              className="min-w-[150px] font-medium"
            >
              <FilterIcon />
              <span>{t("common.addFilter")}</span>
            </Button>
          </TableHeaderWrapper>
          {loading ? (
            <Table>
              <TableSkeleton colSpan={4} />
            </Table>
          ) : products.length ? (
            <Table>
              <TableHeader>
                <TableHead>{t("productsPage.productName")}</TableHead>
                <TableHead>{t("productsPage.brand")}</TableHead>
                <TableHead className="flex items-center gap-2">
                  <span>{t("productsPage.productCode")}</span>
                  <InfoTooltip>{t("productsPage.tooltipText")}</InfoTooltip>
                </TableHead>
                <TableHead>{t("productsPage.source")}</TableHead>
              </TableHeader>
              {loading && <TableSkeleton colSpan={4} />}
              <TableBody>
                {products?.map((product) => (
                  <TableRow
                    role="button"
                    onClick={(e) => onClickItem(e, product.id)}
                    key={product.id}
                  >
                    <TableCell>{product.name}</TableCell>
                    <TableCell>
                      <span className="text-sm text-[#677389]">
                        {product.brand}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-[#677389]">
                        {product.productCode}
                      </span>
                    </TableCell>
                    <TableCell>{product.source}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyTable
              title={t("productsPage.productCouldNotFound")}
              desc={t("productsPage.productCouldNotFoundDesc")}
            />
          )}
          <div className="flex flex-col items-center">
            {filters.totalPages > 1 && (
              <Pagination
                totalPages={filters.totalPages}
                currentPage={filters.page}
                pageSize={filters.size}
                onPageChange={(page) => onPageChange(page)}
              />
            )}
            <PageSizeSelect
              value={filters.size}
              onPageSizeChange={onPageSizeChange}
            />
          </div>
        </div>
      </div>
      <FilterSheet open={openFilterSheet} onOpenChange={setOpenFilterSheet} />

      <Dialog open={isBulkUploadOpen} onOpenChange={setIsBulkUploadOpen}>
        <DialogContent className="h-full min-w-full bg-white">
          <BulkImportWizard
            title={t("clientsPage.importSheet.importProduct")}
            description="Partner kaynağından ürün içe aktarma"
            templateUrl="https://cdn.egaranti.com/static/product_import.csv?v=3"
            expectedColumns={[
              {
                key: "name",
                label: "Ürün Adı (Max 255 Karakter)",
                required: true,
              },
              {
                key: "productCode",
                label: "Ürün Kodu",
                required: false,
              },
              {
                key: "model",
                label: "Model",
                required: false,
              },
              {
                key: "price",
                label: "Fiyat",
                required: false,
                pattern: {
                  regex: /^[0-9]+(\.[0-9]+)?$/,
                  message: "Geçersiz değer",
                },
              },
              {
                key: "priceUnit",
                label: "Para Birimi (TRY,EUR,USD)",
                required: false,
                pattern: {
                  regex: /^(TRY|EUR|USD)$/i,
                  message: "Geçersiz değer",
                },
              },
              {
                key: "warrantyDuration",
                label: "Garanti Süresi (Ay)",
                required: true,
                pattern: {
                  regex: /^[0-9]+$/,
                  message: "Geçersiz değer",
                },
              },
              {
                key: "mainCategoryName",
                label: "Ana Kategori",
                required: true,
              },
              {
                key: "categoryName",
                label: "Kategori",
                required: true,
              },
              {
                key: "subCategoryName",
                label: "Alt Kategori",
                required: true,
              },
              {
                key: "brandName",
                label: "Marka",
                required: true,
              },
              {
                key: "setupRequired",
                label: "Kurulum Gerekli (true,false)",
                required: false,
                pattern: {
                  regex: /^(true|false|)$/i,
                  message: "Geçersiz değer",
                },
              },
              {
                key: "additionalService",
                label: "Ek Hizmet (true,false)",
                required: false,
                pattern: {
                  regex: /^(true|false|)$/i,
                  message: "Geçersiz değer",
                },
              },
            ]}
            onComplete={(data) => {
              bulkCreate(data)
                .then(() => {
                  setIsBulkUploadOpen(false);
                  toast({
                    title: t("clientsPage.importSheet.success"),
                    variant: "success",
                  });
                  getProducts();
                })
                .catch((error) => {
                  console.error("Error bulk creating products:", error);
                  toast({
                    variant: "error",
                    description:
                      error.response?.data?.errors[0]?.message ||
                      "Import işlemi sırasında hata oluştu",
                  });
                });
            }}
          />
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Products;
